import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Plus, Copy, Ban, Link2, Gauge } from "lucide-react";
import { api, formatApiError, fmtBytes, fmtGB } from "../lib/api";
import { GlassCard, PageHeader, QualityBadge, VersionBadge, EmptyState, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { useAuth } from "../context/AuthContext";

export default function Streaming() {
  const { user } = useAuth();
  const [versions, setVersions] = useState([]);
  const [links, setLinks] = useState(null);
  const [versionId, setVersionId] = useState("");
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/vod/versions").then((r) => setVersions(r.data)).catch(() => {});
    api.get("/stream/links").then((r) => setLinks(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  const createLink = async () => {
    if (!versionId) { toast.error("Selecione uma versão"); return; }
    setCreating(true);
    try {
      await api.post("/stream/links", { version_id: versionId, label: label || "Player" });
      toast.success("Link M3U gerado!");
      setLabel("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("URL M3U copiada!"));
  };

  const kill = async (id) => {
    try {
      await api.delete(`/stream/links/${id}`);
      toast.success("Link revogado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="streaming-page">
      <PageHeader eyebrow="Gateway de streaming" title="Links M3U com Billing" subtitle="Cada GB assistido desconta automaticamente do saldo de créditos. Os links usam o domínio atual — aponte um domínio customizado em Ajustes." />

      <TutorialHint
        id="streaming"
        title="Gerando seu primeiro link"
        steps={[
          "Selecione uma versão do catálogo (título + Dublado/Legendado + qualidade).",
          "Clique em Gerar link e copie a URL M3U.",
          "Cole no seu player favorito (VLC, IPTV Smarters, TiviMate...).",
          "O consumo de banda aparece na tabela e desconta do seu saldo em GB.",
          "Revogue um link a qualquer momento com o botão de banir.",
        ]}
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="p-6 anim-fade-up" data-testid="m3u-builder-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
              <Link2 size={20} className="text-cyan-400" />
            </div>
            <h3 className="font-display text-white font-semibold text-sm">Novo link</h3>
          </div>
          <div className="space-y-3">
            <select data-testid="m3u-version-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={versionId} onChange={(e) => setVersionId(e.target.value)}>
              <option value="">Selecione a versão...</option>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.title}{v.season != null ? ` S${String(v.season).padStart(2, "0")}E${String(v.episode).padStart(2, "0")}` : ""} · {v.version.toUpperCase()} · {v.quality}
                </option>
              ))}
            </select>
            <input data-testid="m3u-label-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Rótulo (ex: Sala, Celular...)" value={label} onChange={(e) => setLabel(e.target.value)} />
            <button data-testid="m3u-create-btn" onClick={createLink} disabled={creating} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
              {creating ? <Spinner /> : <><Plus size={15} /> Gerar link M3U</>}
            </button>
          </div>
          <div className="mt-5 p-4 rounded-xl bg-slate-950/50 border border-slate-800/60 flex items-center gap-3">
            <Gauge size={16} className="text-purple-400 shrink-0" />
            <p className="text-xs text-slate-400">Seu saldo atual: <span className="text-white font-semibold" data-testid="streaming-balance">{fmtGB(user.balance_gb)}</span></p>
          </div>
        </GlassCard>

        <div className="lg:col-span-2">
          {links === null ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : links.length === 0 ? (
            <EmptyState icon={Radio} title="Nenhum link ativo" subtitle="Gere seu primeiro link M3U ao lado e comece a assistir." testid="links-empty" />
          ) : (
            <div className="space-y-3" data-testid="links-list">
              {links.map((l) => (
                <GlassCard key={l.id} data-testid={`stream-link-${l.id}`} className={`p-5 anim-fade-up ${!l.active ? "opacity-50" : ""}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{l.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{l.version_info?.title} · {l.owner_email}</p>
                    </div>
                    {l.version_info && (
                      <span className="flex items-center gap-1.5">
                        <VersionBadge version={l.version_info.version} />
                        <QualityBadge quality={l.version_info.quality} />
                      </span>
                    )}
                    <span className="text-[10px] font-mono2 text-slate-500 ml-auto">{fmtBytes(l.usage_bytes)} consumidos</span>
                    {l.active !== false && (
                      <button data-testid={`kill-link-${l.id}`} onClick={() => kill(l.id)} className="p-2 rounded-lg text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/30 transition-colors" title="Revogar link">
                        <Ban size={15} />
                      </button>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <code className="flex-1 text-[11px] font-mono2 text-cyan-300/80 bg-slate-950/60 border border-slate-800/60 rounded-lg px-3 py-2.5 truncate" data-testid={`m3u-url-${l.id}`}>
                      {l.m3u_url}
                    </code>
                    <button data-testid={`m3u-copy-${l.id}`} onClick={() => copy(l.m3u_url)} className="btn-ghost-dark px-3.5 rounded-lg text-slate-300 hover:text-white" title="Copiar URL">
                      <Copy size={15} />
                    </button>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
